<template>
  <div class="hc-dashboard">
    <div class="hc-dashboard__head">
      <div>
        <div class="hc-dashboard__eyebrow">Medhop SaaS Control Center</div>
        <h1 class="hc-dashboard__title">Platform Overview</h1>
        <p class="hc-dashboard__subtitle">
          Kelola tenant, pendaftar, database, dan tagihan dari satu pusat operasional yang aman dan terukur.
        </p>
      </div>
      <div class="hc-dashboard__actions">
        <HcButton :to="{ name: 'registrants-list' }">
          <template #icon><i class="fas fa-list-ul" aria-hidden="true"></i></template>
          Lihat Pendaftar
        </HcButton>
        <HcButton variant="primary" :to="{ name: 'registrants-add' }">
          <template #icon><i class="fas fa-plus" aria-hidden="true"></i></template>
          Tambah Pendaftar
        </HcButton>
      </div>
    </div>

    <div class="hc-dashboard-banner">
      <div class="hc-dashboard-banner__content">
        <span class="hc-dashboard-banner__icon">
          <i class="fas fa-shield-alt" aria-hidden="true"></i>
        </span>
        <div>
          <span class="d-block font-weight-bolder text-dark">Platform operasional siap digunakan</span>
          <span class="text-muted font-size-sm">Ringkasan diperbarui dari layanan Medhop SaaS yang aktif.</span>
        </div>
      </div>
      <HcBadge variant="active"><i class="fas fa-circle font-size-xs" aria-hidden="true"></i> Online</HcBadge>
    </div>

    <div class="row hc-dashboard-grid mb-6">
      <div v-for="item in statistics" :key="item.label" class="col-sm-6 col-xl-3 mb-6">
        <HcStatistic
          :label="item.label"
          :value="loading ? '—' : item.value"
          :icon="item.icon"
          :badge="item.badge"
          :badge-variant="item.badgeVariant"
          trend="Live"
          trend-label="data saat ini"
        />
      </div>
    </div>

    <div class="row hc-dashboard-grid">
      <div class="col-xl-8 mb-6">
        <HcCard title="Distribusi Data Platform" subtitle="Perbandingan total data operasional saat ini">
          <template #actions>
            <HcBadge variant="completed">Real-time</HcBadge>
          </template>
          <apexchart
            type="bar"
            height="320"
            :options="chartOptions"
            :series="chartSeries"
          />
        </HcCard>
      </div>

      <div class="col-xl-4 mb-6">
        <HcCard title="Akses Cepat" subtitle="Kelola modul utama platform">
          <div class="hc-quick-links">
            <router-link v-for="link in quickLinks" :key="link.label" :to="link.to" class="hc-quick-link">
              <span class="hc-quick-link__icon"><i :class="link.icon" aria-hidden="true"></i></span>
              <span class="flex-grow-1">
                <strong class="d-block">{{ link.label }}</strong>
                <small>{{ link.description }}</small>
              </span>
              <i class="fas fa-chevron-right hc-quick-link__arrow" aria-hidden="true"></i>
            </router-link>
          </div>
        </HcCard>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import module from "@/core/modules/CrudModule.js";
import HcBadge from "@/view/components/ui/HcBadge.vue";
import HcButton from "@/view/components/ui/HcButton.vue";
import HcCard from "@/view/components/ui/HcCard.vue";
import HcStatistic from "@/view/components/ui/HcStatistic.vue";

export default {
  name: "Dashboard",
  components: { HcBadge, HcButton, HcCard, HcStatistic },
  data() {
    return {
      loading: true,
      totals: {
        registrants: 0,
        tenants: 0,
        databases: 0,
        billings: 0,
      },
      quickLinks: [
        { label: "Pendaftar", description: "Tinjau onboarding klinik", icon: "fas fa-user-plus", to: { name: "registrants-list" } },
        { label: "Tenant", description: "Kelola tenant aktif", icon: "fas fa-hospital", to: { name: "tenants-list" } },
        { label: "Database", description: "Pantau koneksi database", icon: "fas fa-database", to: { name: "databases-list" } },
        { label: "Pengguna", description: "Atur akses administrator", icon: "fas fa-user-shield", to: { name: "master-users" } },
      ],
    };
  },
  computed: {
    statistics() {
      return [
        { label: "Total Pendaftar", value: this.formatNumber(this.totals.registrants), icon: "fas fa-user-plus", badge: "Onboarding", badgeVariant: "primary" },
        { label: "Tenant Aktif", value: this.formatNumber(this.totals.tenants), icon: "fas fa-hospital", badge: "Aktif", badgeVariant: "active" },
        { label: "Database", value: this.formatNumber(this.totals.databases), icon: "fas fa-database", badge: "Terhubung", badgeVariant: "completed" },
        { label: "Total Tagihan", value: this.formatNumber(this.totals.billings), icon: "fas fa-file-invoice-dollar", badge: "Billing", badgeVariant: "warning" },
      ];
    },
    chartSeries() {
      return [{ name: "Total", data: [this.totals.registrants, this.totals.tenants, this.totals.databases, this.totals.billings] }];
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Inter, Poppins, sans-serif" },
        colors: ["#14608F"],
        plotOptions: { bar: { borderRadius: 8, columnWidth: "44%" } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#E7EDF4", strokeDashArray: 4 },
        xaxis: {
          categories: ["Pendaftar", "Tenant", "Database", "Tagihan"],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: "#6B7280", fontSize: "12px" } },
        },
        yaxis: { labels: { style: { colors: "#6B7280", fontSize: "12px" } } },
        tooltip: { theme: "light" },
      };
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Dashboard" }]);
    this.loadOverview();
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString("id-ID");
    },
    async getTotal(resource) {
      try {
        const response = await module.paginate(resource, "?page=1&page_size=1");
        return response && response.meta && response.meta.pagination
          ? response.meta.pagination.total
          : 0;
      } catch (error) {
        return 0;
      }
    },
    async loadOverview() {
      const values = await Promise.all([
        this.getTotal("registrants"),
        this.getTotal("tenants"),
        this.getTotal("databases"),
        this.getTotal("manage-billings"),
      ]);
      this.totals.registrants = values[0];
      this.totals.tenants = values[1];
      this.totals.databases = values[2];
      this.totals.billings = values[3];
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.hc-quick-links {
  display: grid;
  gap: 10px;
}

.hc-quick-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  color: #1a2433;
  background: #f8fafc;
  border: 1px solid #e7edf4;
  border-radius: 14px;
  transition: transform 200ms ease, border-color 200ms ease, background 200ms ease;
}

.hc-quick-link:hover {
  color: #143263;
  text-decoration: none;
  background: #eef5f8;
  border-color: #cfdde8;
  transform: translateX(3px);
}

.hc-quick-link__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  color: #14608f;
  background: #fff;
  border: 1px solid #dce6ee;
  border-radius: 13px;
  flex-shrink: 0;
}

.hc-quick-link__arrow { color: #94a3b8; font-size: 11px; }
</style>
